import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { SearchX, StickyNote } from "lucide-react";
import qs from "qs";
import moment from "moment";

import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";

import ACL_RELATIONSHIPS from "acl-relationships";
import { queryClient } from "App";
import withRoleCheck from "components/hocs/withRoleCheck";
import { NoteEditingProvider } from "components/NoteEditingContext";
import NoteItem from "components/NoteItem";
import { NotesSkeleton } from "components/NoteItemSkeleton";
import NoteForm from "components/forms/NoteForm";
import { NewAdvancedSearch } from "components/NewAdvancedSearch";
import { useActivityCategories } from "hooks/useActivityCategories";
import ActivityService from "services/Activity";
import UserService from "services/User";
import { createDateTimeFormatter, sortByKey } from "components/helpers";
import { formatUsersForFilter } from "helpers/user";

import "styles/tab_view.scss";

const NoteFormBlock = withRoleCheck("div", [ACL_RELATIONSHIPS.activity.create]);

const formatDateForGrouping = createDateTimeFormatter({ format: "YYYYMM" });

function groupLogByDate(log) {
  const groups = log.reduce((groups, item) => {
    const date = formatDateForGrouping(item.created_at);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(item);
    return groups;
  }, {});
  return Object.keys(groups).map(date => {
    return {
      date,
      items: groups[date],
    };
  });
}

function formatDateGroup(date) {
  return moment(date, "YYYYMM").format("MMMM YYYY");
}

const fetchUsers = async () =>
  formatUsersForFilter(await UserService.getUsers());

const fetchNotes = async ({ queryKey = [] }) => {
  const [, modelId, modelType, slug, filters] = queryKey;
  const slugOpts = slug ? { slug, modelId } : undefined;

  return ActivityService.getActivityLogs(modelId, modelType, filters, slugOpts);
};

function NotesView({ modelType, modelId, slug, handleTabChange, callSid }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlParams = useMemo(
    () => qs.parse(searchParams.toString()),
    [searchParams]
  );

  const { categories, refetchCategories } = useActivityCategories();
  const { data: users } = useQuery({
    initialData: [],
    queryKey: ["users"],
    queryFn: fetchUsers,
  });

  const filters = useMemo(() => {
    const categoryOptions = categories.map(({ key: id, text: name }) => ({
      id,
      name,
    }));
    return [
      {
        key: "Category",
        title: "Category",
        type: "select",
        data: categoryOptions,
      },
      { key: "User", title: "Created By", type: "select", data: users },
      { key: "Mention", title: "Mention", type: "select", data: users },
      { key: "Has Attachment", title: "Has Attachment", type: "ruvixxToggle" },
    ];
  }, [categories, users]);

  // NotesView shares the URL with sibling tabs, so restrict the params this view
  // owns to its own filter keys: a sibling tab's params must not reach the notes
  // endpoint as filters for nothing.
  const notesKeys = useMemo(() => filters.map(f => f.key), [filters]);
  const queryParams = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(urlParams).filter(([key]) =>
          notesKeys.some(k => key === k || key.startsWith(`${k}:`))
        )
      ),
    [urlParams, notesKeys]
  );

  const {
    data: notes = [],
    isLoading: isLoadingNotes,
    refetch: refetchNotes,
  } = useQuery({
    queryKey: ["notes", modelId, modelType, slug, queryParams],
    queryFn: fetchNotes,
    enabled: !!modelId,
  });

  const sortedLog = sortByKey(notes, "created_at", true);
  const groupedLog = sortByKey(groupLogByDate(sortedLog), "date", true);

  const hasActiveFilters = Object.keys(queryParams).length > 0;

  // Replaces the notes filters while preserving any sibling tab's params.
  function handleApplyFilters(params) {
    const others = Object.fromEntries(
      Object.entries(urlParams).filter(([key]) => !(key in queryParams))
    );
    setSearchParams(qs.stringify({ ...others, ...params }));
  }

  async function invalidateNotesAndCategories() {
    await queryClient.invalidateQueries({
      queryKey: ["notes", modelId, modelType, slug, queryParams],
    });
    await queryClient.invalidateQueries({ queryKey: ["activity-categories"] });
  }

  return (
    <div className="tw:flex tw:flex-col tw:w-full">
      <div className="tw:mt-4 tw:mb-4 tw:flex tw:justify-end">
        <NewAdvancedSearch
          filters={filters}
          queryParams={queryParams}
          tableName="notes"
          onApply={handleApplyFilters}
        />
      </div>

      <NoteFormBlock className="tw:mb-6 tw:flex">
        <NoteForm
          callSid={callSid}
          modelType={modelType}
          modelId={modelId}
          slug={slug}
          updateCategories={refetchCategories}
          categories={categories}
          onSuccess={refetchNotes}
        />
      </NoteFormBlock>

      {isLoadingNotes ? (
        <NotesSkeleton />
      ) : notes.length === 0 ? (
        <Empty className="tw:py-12">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              {hasActiveFilters ? <SearchX /> : <StickyNote />}
            </EmptyMedia>
            <EmptyTitle>
              {hasActiveFilters ? "No matching notes" : "No notes yet"}
            </EmptyTitle>
            <EmptyDescription>
              {hasActiveFilters
                ? "No notes match the current filters. Try adjusting or clearing the filters."
                : "Notes added to this record will appear here."}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <NoteEditingProvider>
          {groupedLog.map(({ date, items }) => (
            <div key={date} className="tw:mb-6">
              <h3 className="tw:mb-3 tw:font-heading tw:text-base tw:font-medium tw:text-[var(--color-text-primary)]">
                {formatDateGroup(date)}
              </h3>
              <div className="tw:flex tw:flex-col tw:gap-3">
                {items.map(note => (
                  <NoteItem
                    key={note.id}
                    note={note}
                    categories={categories}
                    handleTabChange={handleTabChange}
                    onChange={invalidateNotesAndCategories}
                    slug={slug}
                    modelId={modelId}
                  />
                ))}
              </div>
            </div>
          ))}
        </NoteEditingProvider>
      )}
    </div>
  );
}

export default NotesView;
